/**
 * sl-9qm.10.3: FormView Component
 *
 * Public form ViewComponent that renders form fields, DSGVO consent,
 * and submits to the backend /p/ endpoint.
 *
 * SOLID:
 * - S: Only renders and submits the public form
 * - O: Field types extensible via FormFieldConfig
 * - D: Uses FormConfig abstraction, not implementation details
 */
import { createElement, useCallback, useState } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  FormConfig,
  FormFieldConfig,
} from '../../../types/form-config.types';
import { formConfigSchema } from '../../../types/form-config.types';
import type { ViewComponent } from '../../../types/view-component.types';
import type {
  BoundDataRow,
  ViewRuntimeContext,
} from '../../../types/view-component.types';
import { resolveCustomAppRuntimeHref } from '../runtime-href';
import * as styles from '../styles.css';

// ─── Config Schema ──────────────────────────────────────────────────

const configSchema = z.object({
  /** Backend base URL for /p/ routes (auto-detected from window.location) */
  backendBaseUrl: z.string().default(''),
});

// ─── Form Field Renderer ────────────────────────────────────────────

function renderFormField(
  field: FormFieldConfig,
  value: unknown,
  error: string | undefined,
  onChange: (columnId: string, value: unknown) => void,
  actionId: string | null
) {
  const errorEl = error
    ? createElement('div', { className: styles.formFieldError }, error)
    : null;

  const helpEl = field.helpText
    ? createElement('div', { className: styles.formHelpText }, field.helpText)
    : null;

  const label = createElement(
    'label',
    { className: styles.formLabel },
    field.label,
    field.validation?.required
      ? createElement('span', { className: styles.formRequired }, '*')
      : null
  );

  let input: ReturnType<typeof createElement>;

  switch (field.type) {
    case 'textarea':
      input = createElement('textarea', {
        name: field.columnId,
        className: styles.formTextarea,
        value: (value as string) ?? '',
        placeholder: field.placeholder ?? '',
        onChange: (e: { target: { value: string } }) =>
          onChange(field.columnId, e.target.value),
      });
      break;

    case 'number':
      input = createElement('input', {
        name: field.columnId,
        type: 'number',
        className: styles.formInput,
        value: value !== null && value !== undefined ? String(value) : '',
        placeholder: field.placeholder ?? '',
        min: field.validation?.min,
        max: field.validation?.max,
        onChange: (e: { target: { value: string } }) =>
          onChange(
            field.columnId,
            e.target.value === '' ? null : Number(e.target.value)
          ),
      });
      break;

    case 'email':
      input = createElement('input', {
        name: field.columnId,
        type: 'email',
        className: styles.formInput,
        value: (value as string) ?? '',
        placeholder: field.placeholder ?? 'email@example.com',
        onChange: (e: { target: { value: string } }) =>
          onChange(field.columnId, e.target.value),
      });
      break;

    case 'url':
      input = createElement('input', {
        name: field.columnId,
        type: 'url',
        className: styles.formInput,
        value: (value as string) ?? '',
        placeholder: field.placeholder ?? 'https://',
        onChange: (e: { target: { value: string } }) =>
          onChange(field.columnId, e.target.value),
      });
      break;

    case 'date':
      input = createElement('input', {
        name: field.columnId,
        type: 'date',
        className: styles.formInput,
        value: (value as string) ?? '',
        onChange: (e: { target: { value: string } }) =>
          onChange(field.columnId, e.target.value),
      });
      break;

    case 'select':
      input = createElement(
        'select',
        {
          name: field.columnId,
          className: styles.formSelect,
          value: (value as string) ?? '',
          onChange: (e: { target: { value: string } }) =>
            onChange(field.columnId, e.target.value),
        },
        createElement('option', { value: '' }, '-- Select --'),
        ...(field.options ?? []).map(opt =>
          createElement('option', { key: opt, value: opt }, opt)
        )
      );
      break;

    case 'multi-select': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      input = createElement(
        'div',
        {
          style: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sl-custom-app-gap-xs)',
          },
        },
        ...(field.options ?? []).map(opt =>
          createElement(
            'label',
            {
              key: opt,
              style: {
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sl-custom-app-gap-xs)',
              },
            },
            createElement('input', {
              name: field.columnId,
              type: 'checkbox',
              checked: selected.includes(opt),
              onChange: (e: { target: { checked: boolean } }) => {
                const next = e.target.checked
                  ? [...selected, opt]
                  : selected.filter(s => s !== opt);
                onChange(field.columnId, next);
              },
            }),
            opt
          )
        )
      );
      break;
    }

    case 'checkbox':
      input = createElement(
        'label',
        {
          style: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sl-custom-app-gap-sm)',
          },
        },
        createElement('input', {
          name: field.columnId,
          type: 'checkbox',
          checked: Boolean(value),
          onChange: (e: { target: { checked: boolean } }) =>
            onChange(field.columnId, e.target.checked),
        }),
        field.placeholder ?? ''
      );
      break;

    default:
      // text and fallback
      input = createElement('input', {
        name: field.columnId,
        type: 'text',
        className: styles.formInput,
        value: (value as string) ?? '',
        placeholder: field.placeholder ?? '',
        maxLength: field.validation?.maxLength,
        onChange: (e: { target: { value: string } }) =>
          onChange(field.columnId, e.target.value),
      });
  }

  return createElement(
    'div',
    {
      key: field.columnId,
      className: styles.formFieldGroup,
      'data-sl-field': field.columnId,
      'data-sl-field-type': field.type,
      ...(actionId
        ? { 'data-sl-control': `action:${actionId}:field:${field.columnId}` }
        : {}),
    },
    label,
    input,
    helpEl,
    errorEl
  );
}

// ─── Form State Hook ────────────────────────────────────────────────

type FormState = 'idle' | 'submitting' | 'success' | 'error';

interface CustomAppFormBridge {
  execute(command: {
    controlId: string;
    payload: Record<string, unknown>;
  }): Promise<Record<string, unknown>>;
}

function readCustomAppFormBridge(): CustomAppFormBridge | null {
  const candidate = (
    window as typeof window & { StreamLainCustomApp?: unknown }
  ).StreamLainCustomApp;
  if (!candidate || typeof candidate !== 'object') return null;
  const bridge = candidate as Partial<CustomAppFormBridge>;
  return typeof bridge.execute === 'function'
    ? (bridge as CustomAppFormBridge)
    : null;
}

function useFormSubmit(
  actionId: string | null,
  submitAction: FormConfig['submitAction'],
  runtimeContext?: ViewRuntimeContext
) {
  const [state, setState] = useState<FormState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const submit = useCallback(
    async (
      fields: Record<string, unknown>,
      consent: boolean,
      honeypot: string
    ) => {
      setState('submitting');
      setErrorMessage(null);
      try {
        const bridge = readCustomAppFormBridge();
        if (!bridge || !actionId) {
          throw new Error('Formular-Aktion ist nicht verfügbar');
        }
        const result = await bridge.execute({
          controlId: `action:${actionId}`,
          payload: {
            fields,
            consent,
            _hp: honeypot,
          },
        });
        const redirectUrl =
          typeof result['redirectUrl'] === 'string'
            ? result['redirectUrl']
            : submitAction.type === 'redirect'
              ? submitAction.url
              : null;
        if (redirectUrl) {
          window.location.assign(
            resolveCustomAppRuntimeHref(redirectUrl, runtimeContext)
          );
          return;
        }
        setState('success');
        setSuccessMessage(
          typeof result['message'] === 'string'
            ? result['message']
            : submitAction.type === 'message'
              ? submitAction.message
              : 'Gesendet'
        );
      } catch (err) {
        setState('error');
        setErrorMessage(
          err instanceof Error ? err.message : 'An unexpected error occurred'
        );
      }
    },
    [actionId, runtimeContext, submitAction]
  );

  return { state, errorMessage, successMessage, submit };
}

// ─── Client-Side Validation ─────────────────────────────────────────

function validateFields(
  fields: Record<string, unknown>,
  fieldConfigs: FormFieldConfig[]
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const config of fieldConfigs) {
    const value = fields[config.columnId];
    const v = config.validation;

    if (
      v?.required &&
      (value === null || value === undefined || value === '')
    ) {
      errors[config.columnId] = `${config.label} is required`;
      continue;
    }

    if (value === null || value === undefined || value === '') continue;

    if (
      (config.type === 'text' || config.type === 'textarea') &&
      typeof value === 'string'
    ) {
      if (v?.minLength && value.length < v.minLength) {
        errors[config.columnId] =
          `${config.label} must be at least ${v.minLength} characters`;
      }
      if (v?.maxLength && value.length > v.maxLength) {
        errors[config.columnId] =
          `${config.label} must be at most ${v.maxLength} characters`;
      }
      if (v?.pattern && !new RegExp(v.pattern).test(value)) {
        errors[config.columnId] =
          v.patternMessage ?? `${config.label} has an invalid format`;
      }
    }

    if (
      config.type === 'email' &&
      typeof value === 'string' &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ) {
      errors[config.columnId] = `${config.label} must be a valid email`;
    }

    if (config.type === 'number' && typeof value === 'number') {
      if (v?.min !== undefined && value < v.min) {
        errors[config.columnId] = `${config.label} must be at least ${v.min}`;
      }
      if (v?.max !== undefined && value > v.max) {
        errors[config.columnId] = `${config.label} must be at most ${v.max}`;
      }
    }
  }

  return errors;
}

function resolveFormConfigFromData(
  data: BoundDataRow[],
  runtimeContext?: ViewRuntimeContext
): FormConfig | null {
  const runtimeSource = readRuntimeSource(runtimeContext);
  const rowCells = runtimeContext?.row?.cells;
  const candidates = [
    data[0]?.['formConfig'],
    readCellByColumnName(data[0], runtimeSource?.columns, 'formConfig'),
    data[0],
    runtimeContext?.row?.cells['formConfig'],
    readCellByColumnName(rowCells, runtimeSource?.columns, 'formConfig'),
    readCellByColumnName(
      runtimeSource?.rows?.[0]?.cells,
      runtimeSource?.columns,
      'formConfig'
    ),
    runtimeContext?.row?.cells,
  ];

  for (const candidate of candidates) {
    const parsed = formConfigSchema.safeParse(
      parseFormConfigCandidate(candidate)
    );
    if (parsed.success) return parsed.data;
  }

  return null;
}

function readRuntimeSource(runtimeContext?: ViewRuntimeContext): {
  columns?: Array<{ id?: unknown; name?: unknown }>;
  rows?: Array<{ cells?: Record<string, unknown> }>;
} | null {
  const source = runtimeContext?.source;
  if (!source || typeof source !== 'object') return null;
  return source as {
    columns?: Array<{ id?: unknown; name?: unknown }>;
    rows?: Array<{ cells?: Record<string, unknown> }>;
  };
}

function readCellByColumnName(
  cells: Record<string, unknown> | undefined,
  columns: Array<{ id?: unknown; name?: unknown }> | undefined,
  columnName: string
): unknown {
  if (!cells || !columns) return undefined;
  const column = columns.find(item => item.name === columnName);
  if (typeof column?.id !== 'string') return undefined;
  return cells[column.id];
}

function parseFormConfigCandidate(candidate: unknown): unknown {
  if (typeof candidate !== 'string') return candidate;
  try {
    return JSON.parse(candidate);
  } catch {
    return candidate;
  }
}

function resolveFormActionId(
  runtimeContext: ViewRuntimeContext | undefined,
  formConfig: FormConfig
): string | null {
  const customAppConfig = runtimeContext?.customApp?.['config'];
  if (!customAppConfig || typeof customAppConfig !== 'object') return null;
  const config = customAppConfig as Record<string, unknown>;
  const artifact = readRecord(config['customAppArtifact']);
  const manifest =
    readRecord(artifact?.['manifest']) ?? readRecord(config['customApp']);
  const actions = Array.isArray(manifest?.['actions'])
    ? manifest['actions']
    : [];
  const bindings = readRecord(config['databaseBindings']);
  const expectedFieldIds = new Set(
    formConfig.fields.map(field => field.columnId)
  );

  for (const candidate of actions) {
    const action = readRecord(candidate);
    if (!action || action['type'] !== 'rowInsert') continue;
    const id = typeof action['id'] === 'string' ? action['id'] : null;
    const sourceKey =
      typeof action['sourceKey'] === 'string' ? action['sourceKey'] : null;
    if (!id || !sourceKey) continue;
    const fieldIds = new Set(
      (Array.isArray(action['fields']) ? action['fields'] : [])
        .map(field => readRecord(field)?.['columnId'])
        .filter((columnId): columnId is string => typeof columnId === 'string')
    );
    if (![...expectedFieldIds].every(columnId => fieldIds.has(columnId))) {
      continue;
    }
    const binding = readRecord(bindings?.[sourceKey]);
    const targetMatches =
      (!formConfig.targetDatabaseDocId ||
        binding?.['docId'] === formConfig.targetDatabaseDocId) &&
      (!formConfig.targetDatabaseBlockId ||
        (binding?.['blockId'] ?? binding?.['databaseId']) ===
          formConfig.targetDatabaseBlockId);
    if (targetMatches) return id;
  }
  return null;
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

// ─── Form Render Function ───────────────────────────────────────────

function FormViewRenderer({
  formConfig,
  mode,
  actionId,
  runtimeContext,
}: {
  formConfig: FormConfig;
  mode: string;
  actionId: string | null;
  runtimeContext?: ViewRuntimeContext;
}) {
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>(
    () => {
      const initial: Record<string, unknown> = {};
      for (const field of formConfig.fields) {
        initial[field.columnId] = field.defaultValue ?? null;
      }
      return initial;
    }
  );
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { state, errorMessage, successMessage, submit } = useFormSubmit(
    actionId,
    formConfig.submitAction,
    runtimeContext
  );

  const handleFieldChange = useCallback((columnId: string, value: unknown) => {
    setFieldValues(prev => ({ ...prev, [columnId]: value }));
    setFieldErrors(prev => {
      if (!prev[columnId]) return prev;
      const next = { ...prev };
      delete next[columnId];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    (e: { preventDefault: () => void }) => {
      e.preventDefault();

      // Client-side validation
      const errors = validateFields(fieldValues, formConfig.fields);

      // Consent check
      if (formConfig.consent.requireExplicitConsent && !consent) {
        errors['__consent'] = 'Consent is required';
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }

      void submit(fieldValues, consent, honeypot);
    },
    [fieldValues, formConfig, consent, honeypot, submit]
  );

  // Success state
  if (state === 'success') {
    return createElement(
      'div',
      { className: styles.formView },
      createElement(
        'div',
        { className: styles.formSuccessMessage },
        successMessage
      )
    );
  }

  const isActionable = mode !== 'builder' && Boolean(actionId);

  return createElement(
    'form',
    {
      className: styles.formView,
      onSubmit: isActionable
        ? handleSubmit
        : (e: { preventDefault: () => void }) => e.preventDefault(),
      noValidate: true,
      ...(actionId ? { 'data-sl-control': `action:${actionId}` } : {}),
    },
    // Title
    formConfig.title
      ? createElement('h2', { className: styles.formTitle }, formConfig.title)
      : null,

    // Description
    formConfig.description
      ? createElement(
          'p',
          { className: styles.formDescription },
          formConfig.description
        )
      : null,

    // Error message (submission error)
    errorMessage
      ? createElement(
          'div',
          { className: styles.formErrorMessage },
          errorMessage
        )
      : null,

    // Form-level validation error
    fieldErrors['__form']
      ? createElement(
          'div',
          { className: styles.formErrorMessage },
          fieldErrors['__form']
        )
      : null,

    // Fields
    ...formConfig.fields.map(field =>
      renderFormField(
        field,
        fieldValues[field.columnId],
        fieldErrors[field.columnId],
        handleFieldChange,
        actionId
      )
    ),

    // Honeypot (invisible trap field)
    formConfig.spamProtection.honeypot
      ? createElement('input', {
          className: styles.formHoneypot,
          type: 'text',
          name: '_hp',
          tabIndex: -1,
          autoComplete: 'off',
          value: honeypot,
          onChange: (e: { target: { value: string } }) =>
            setHoneypot(e.target.value),
          'aria-hidden': 'true',
        })
      : null,

    // DSGVO Consent
    formConfig.consent.requireExplicitConsent
      ? createElement(
          'div',
          { className: styles.formCheckboxRow },
          createElement('input', {
            type: 'checkbox',
            name: 'consent',
            checked: consent,
            onChange: (e: { target: { checked: boolean } }) =>
              setConsent(e.target.checked),
            style: { marginTop: 'var(--sl-custom-app-gap-xxs)', flexShrink: 0 },
          }),
          createElement(
            'div',
            { className: styles.formConsentText },
            formConfig.consent.consentText,
            formConfig.consent.privacyPolicyUrl
              ? createElement(
                  'span',
                  null,
                  ' ',
                  createElement(
                    'a',
                    {
                      href: resolveCustomAppRuntimeHref(
                        formConfig.consent.privacyPolicyUrl,
                        runtimeContext
                      ),
                      ...(formConfig.consent.privacyPolicyUrl.startsWith('/')
                        ? {}
                        : {
                            target: '_blank',
                            rel: 'noopener noreferrer',
                          }),
                      className: styles.formConsentLink,
                    },
                    runtimeContext?.locale === 'de'
                      ? 'Datenschutzerklärung'
                      : 'Privacy Policy'
                  )
                )
              : null
          )
        )
      : null,

    // Consent error
    fieldErrors['__consent']
      ? createElement(
          'div',
          { className: styles.formFieldError },
          fieldErrors['__consent']
        )
      : null,

    // Submit button
    createElement(
      'button',
      {
        type: 'submit',
        className: styles.formSubmitButton,
        'data-sl-role': 'submit',
        ...(actionId ? { 'data-sl-action': actionId } : {}),
        disabled: state === 'submitting' || !isActionable,
      },
      state === 'submitting'
        ? 'Submitting...'
        : (formConfig.submitLabel ?? 'Submit')
    ),

    // Data retention notice
    formConfig.consent.dataRetentionNotice
      ? createElement(
          'div',
          {
            style: {
              fontSize: 'var(--sl-custom-app-font-size-xs)',
              color: 'var(--streamlain-text-disable-color)',
              marginTop: 'var(--sl-custom-app-gap-xs)',
              textAlign: 'center' as const,
            },
          },
          formConfig.consent.dataRetentionNotice
        )
      : null
  );
}

// ─── ViewComponent Definition ───────────────────────────────────────

export const FormViewComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.FORM_VIEW,
  type: 'widget',
  name: 'Form',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    '\u2709'
  ),
  capabilities: [
    {
      role: 'fields',
      description: 'Form input fields bound to writable columns',
      required: true,
      requirements: [
        {
          field: 'fields',
          acceptedTypes: ['text', 'number', 'checkbox', 'select', 'date'],
          required: true,
          cardinality: 'many',
        },
      ],
    },
    {
      role: 'submit',
      description: 'Submit button label (optional override)',
      required: false,
      requirements: [
        {
          field: 'submitLabel',
          acceptedTypes: ['text'],
          required: false,
        },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    backendBaseUrl: '',
  },
  render({ config, data, mode, runtimeContext }) {
    // FormConfig comes from ViewConfig.settings.formConfig (view-level)
    // We access it via window.__SL_VIEW_CONFIG__ in public mode,
    // or it's passed through the render props in builder/internal mode.
    const typedConfig = config as z.infer<typeof configSchema> & {
      formConfig?: FormConfig;
    };

    const formConfig =
      resolveFormConfigFromData(data, runtimeContext) ?? typedConfig.formConfig;
    if (!formConfig || !formConfig.enabled) {
      return createElement(
        'div',
        {
          className: styles.formView,
          style: {
            textAlign: 'center' as const,
            color: 'var(--streamlain-text-secondary-color)',
            padding: 'var(--sl-custom-app-padding-xl)',
          },
        },
        mode === 'builder'
          ? 'Form not configured — open View Settings to set up the form.'
          : 'Form is not available.'
      );
    }

    return createElement(FormViewRenderer, {
      formConfig,
      mode,
      actionId: resolveFormActionId(runtimeContext, formConfig),
      runtimeContext,
    });
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      '\u2709 Form'
    );
  },
};
